import Vue from 'vue';
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { IMemberPosition } from '../interfaces/member-position.interface';
import { APP_SOCKET_MESSAGES_TYPE, APP_STORE_MUTATIONS, MEMBERS_POSITION_COLORS, _APP_STORE } from '../values/app.values';
import { COLORS_BASE, FOOTER_TYPE } from '../values/core.values';
import ToolService from '@/providers/tools';
import { AppServices } from '../services/app.services';
import { BackupFileServices } from '../services/backup.file.services';

export const APP_STORE = _APP_STORE;

@Module({ namespaced: true })
export class AppStore extends VuexModule {

  /* State */
  public sidebarShown: boolean = true;
  public usingRouter: boolean = true;
  public activeSchoolDaysLoaded: boolean = false;
  public institutionsLoaded: boolean = false;
  public expIndexDBDocumentTime: number = 0;
  public activeIndexDB: boolean = false;
  public fileExtensionsNotAllowed: string[] = [];
  public userMode: string = null;
  public hasSidebarButton: boolean = false;
  public webview : boolean = false;
  public offlineMode : boolean = false;
  public errorQueue: any[] = [];

  public footer = {
    type: FOOTER_TYPE.COMMON
  };

  public copyrights = {
    color: COLORS_BASE.COMMON
  };

  public header = {
    visible: true
  };

  public socketConnected: boolean = false;

  public userSockets = {
    onUpdatePosition: null,
    onHome: null
  }

  public userPosition: IMemberPosition = {
    classId: null,
    courseId: null,
    sectionId: null,
    isFolder: null,
    homeworkId: null,
  }

  public membersPosition: IMemberPosition[] = [];
  public colors: string[] = [...MEMBERS_POSITION_COLORS];
  public presentationSocketConnected : boolean = false;
  public isBoardPreviousDisconnectionSocket : boolean = false;
  public isPreviousDisconnectionSocket : boolean = false;
  public showConnectManuallyButton : boolean = false;
  public appReady: boolean = false;
  public expandCalendar: boolean = false;
  public isSubjectBuilderActive: boolean = false;

  /* Mutations */
  @Mutation
  public setInstitutionsLoaded(value: boolean) {
    this.institutionsLoaded = value;
  }

  @Mutation
  public setActiveSchoolDaysLoaded(value: boolean) {
    this.activeSchoolDaysLoaded = value;
  }

  @Mutation
  public setCopyrightsColor(color: string) {
    this.copyrights.color = color;
  }

  @Mutation
  public changeRouter(usingRouter: boolean) {
    this.usingRouter = usingRouter;
  }

  @Mutation
  public toggleSidebar() {
    this.sidebarShown = !this.sidebarShown;
  }

  @Mutation
  public showSidebar() {
    this.sidebarShown = true;
  }

  @Mutation
  public changeFooterType(type: string) {
    this.footer.type = type;
  }

  @Mutation
  public setHeaderVisible(visible: boolean) {
    this.header.visible = visible;
  }

  @Mutation
  public setActiveWebwiew(active: boolean) {
    this.webview = active;
  }

  @Mutation
  public setExpIndexDBDocumentTime(expTime: number) {
    this.expIndexDBDocumentTime = expTime;
  }

  @Mutation
  public setActiveIndexDB(data: any) {
    this.activeIndexDB = data;
  }

  @Mutation
  public setFileExtNotAllowed(extensions: string[]) {
    this.fileExtensionsNotAllowed = extensions;
  }

  @Mutation
  public setUserPosition({classId, courseId, sectionId, isFolder, homeworkId}) {
    this.userPosition.classId = classId;
    this.userPosition.courseId = courseId;
    this.userPosition.sectionId = sectionId;
    this.userPosition.isFolder = isFolder;
    this.userPosition.homeworkId = homeworkId;
    let toRemove = [...this.membersPosition.filter(member => member.classId !== classId)];
    this.membersPosition = this.membersPosition.filter(member => member.classId === classId);
    toRemove.forEach(m => this.colors.unshift(m.color));
  }

  @Mutation
  public setUserSocket({key, socket}) {
    this.userSockets[key] = socket;
  }

  @Mutation
  public setMemberPosition(data: IMemberPosition) {
    let found = this.membersPosition
      .findIndex(m => m.username == data.username);
    if(found === -1) {
      let color = this.colors.length > 0 ? this.colors.splice(0, 1)[0] : ToolService.getRandomColor();
      this.membersPosition.push({
        ...data, color
      });
    } else {
      switch(data.messageType) {
        case APP_SOCKET_MESSAGES_TYPE.LEAVE:
          this.colors.unshift(this.membersPosition[found].color);
          this.membersPosition.splice(found, 1);
        break;
        case APP_SOCKET_MESSAGES_TYPE.UPDATE_LOCATION:
        case APP_SOCKET_MESSAGES_TYPE.ON_HOME:
          Vue.set(this.membersPosition[found], 'classId', data.classId);
          Vue.set(this.membersPosition[found], 'courseId', data.courseId);
          Vue.set(this.membersPosition[found], 'sectionId', data.sectionId);
          Vue.set(this.membersPosition[found], 'isFolder', data.isFolder);
        break;
      }
    }
    Vue.set(this, 'membersPosition', this.membersPosition);
  }

  @Mutation
  public setSocketConnected(value: boolean) {
    this.socketConnected = value;
  }

  @Mutation
  public cleanSocketsAndMembers() {
    Vue.prototype.$EdSocket.off(this.userSockets.onHome);
    Vue.prototype.$EdSocket.off(this.userSockets.onUpdatePosition);
    this.userSockets.onHome = null;
    this.userSockets.onUpdatePosition = null;
    this.membersPosition = [];
    this.colors = [...MEMBERS_POSITION_COLORS];
    this.userPosition.classId = null;
    this.userPosition.courseId = null;
    this.userPosition.sectionId = null;
    this.userPosition.homeworkId = null;
    this.userPosition.isFolder = null;
  }

  @Mutation
  public setPresentationSocketConnected(value : boolean){
    this.presentationSocketConnected = value;
  }

  @Mutation
  public setUserMode(value: string){
    this.userMode = value;
  }

  @Mutation
  public updatePreviousDisconnectionSocket(isPreviousDisconnectionSocket : boolean){
    this.isPreviousDisconnectionSocket = isPreviousDisconnectionSocket;
  }

  @Mutation
  public updateShowConnectManuallyButton(showConnectManuallyButton : boolean){
    this.showConnectManuallyButton = showConnectManuallyButton;
  }

  @Mutation
  public setSidebarButton(value: boolean) {
    this.hasSidebarButton = value;
  }

  @Mutation
  public setAppReady(value: boolean) {
    this.appReady = value;
  }

  @Mutation
  public setSubjectBuilderActive(value: boolean) {
    this.isSubjectBuilderActive = value;
  }

  @Mutation
  public updateBoardPreviousDisconnectionSocket(isBoardPreviousDisconnectionSocket : boolean){
    this.isBoardPreviousDisconnectionSocket = isBoardPreviousDisconnectionSocket;
  }

  @Mutation
  public setOfflineMode(offlineMode: boolean) : void {
    this.offlineMode = offlineMode;
  }


  @Mutation
  public setExpandCalendar(expand:boolean) {
    this.expandCalendar = expand;
  }

  @Mutation
  public addErrorLog(errorLog:any) {
    this.errorQueue.push(errorLog);
  }

  @Mutation
  public cleanErrorLog() {
    this.errorQueue = [];
  }

  /* Actions */
  @Action({})
  public sendTeacherPosition(data: IMemberPosition) : Promise<void> {
    return new Promise((resolve) => {
      this.context.commit(APP_STORE_MUTATIONS.SET_USER_POSITION, data);
      resolve();
    });
  }

  @Action({})
  public cleanTeacherPosition() : Promise<void> {
    return new Promise((resolve) => {
      if (this.userPosition.classId) {
        this.context.commit(APP_STORE_MUTATIONS.CLEAN_SOCKETS_AND_MEMBERS);
      }
      resolve();
    });
  }

  @Action({rawError:true})
  public async getInitialData(): Promise<any>{
    const data = await AppServices.getInitialData();

    await Vue.prototype.$App.Store.setFileExtNotAllowed(data.fileExtensionsNotAllowed);
    return data;
  }

  /* Initial State */
  @Mutation
  public initState(state) {
    this.sidebarShown = true;
    this.usingRouter = true;
    this.activeSchoolDaysLoaded = false;
    this.institutionsLoaded = false;
    this.expIndexDBDocumentTime = 0;
    this.activeIndexDB = false;
    this.fileExtensionsNotAllowed = [];
    this.userMode = null;
    this.hasSidebarButton = false;
    this.webview = false;
    this.offlineMode = false;
    this.errorQueue = [];
    this.footer = {
      type: FOOTER_TYPE.COMMON
    };
    this.copyrights = {
      color: COLORS_BASE.COMMON
    };
    this.header = {
      visible: true
    };
    this.socketConnected = false;
    this.userSockets = {
      onUpdatePosition: null,
      onHome: null
    }
    this.userPosition = {
      classId: null,
      courseId: null,
      sectionId: null,
      isFolder: null,
      homeworkId: null,
    }
    this.membersPosition = [];
    this.colors = [...MEMBERS_POSITION_COLORS];
    this.presentationSocketConnected = false;
    this.isBoardPreviousDisconnectionSocket = false;
    this.isPreviousDisconnectionSocket = false;
    this.showConnectManuallyButton = false;
    this.appReady = false;
    this.expandCalendar = false;
    this.isSubjectBuilderActive = false;
  }
}
